import { useState, useMemo } from 'react'
import { Loader2, AlertCircle, Info, CheckCircle2 } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Slider } from '@/components/ui/slider'
import { useParty } from '@/hooks/useParty'
import { useRegionsByNation } from '@/hooks/useRegionsByNation'
import { useActiveScandals } from '@/hooks/useActiveScandals'
import { useRecentSuccesses } from '@/hooks/useRecentSuccesses'
import { useCreateAction } from '@/hooks/useCreateAction'
import { cn } from '@/lib/utils'
import {
  PR_SUBTYPES,
  PR_EFFECTS,
  DAMAGE_CONTROL_STRATEGIES,
  DAMAGE_CONTROL_STRATEGY_ORDER,
  MESSAGE_THEMES,
  calculateGeneralMessagingEffects,
  calculateDamageControlEffects,
  calculateHighlightSuccessEffects,
  type PRSubtype,
  type PRFocus,
  type DamageControlStrategy,
  type AmplificationScope,
} from '@/constants/pr'
import { SCANDAL_TYPES, type ScandalSeverity } from '@/constants/attacks'

interface PRActionFormProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  partyId: string
  currentTick: number
  availableAP: number
  onSuccess?: () => void
  onBack?: () => void
}

export function PRActionForm({
  open,
  onOpenChange,
  partyId,
  currentTick,
  availableAP,
  onSuccess,
  onBack,
}: PRActionFormProps) {
  if (!open) return null

  return (
    <PRActionFormContent
      open={open}
      onOpenChange={onOpenChange}
      partyId={partyId}
      currentTick={currentTick}
      availableAP={availableAP}
      onSuccess={onSuccess}
      onBack={onBack}
    />
  )
}

function PRActionFormContent({
  open,
  onOpenChange,
  partyId,
  currentTick,
  availableAP,
  onSuccess,
  onBack,
}: PRActionFormProps) {
  // PR type selection
  const [prType, setPrType] = useState<PRSubtype>('general_messaging')

  // General messaging fields
  const [focus, setFocus] = useState<PRFocus>('balanced')
  const [regionId, setRegionId] = useState<string>('')
  const [theme, setTheme] = useState<string>('')

  // Damage control fields
  const [scandalId, setScandalId] = useState<string>('')
  const [strategy, setStrategy] = useState<DamageControlStrategy>('apologize')

  // Highlight success fields
  const [successEventId, setSuccessEventId] = useState<string>('')
  const [amplificationScope, setAmplificationScope] = useState<AmplificationScope>('national')

  // Common fields
  const [intensity, setIntensity] = useState<number>(1)

  // Fetch current party to get nation_id
  const { party, isLoading: partyLoading } = useParty(partyId)

  // Fetch regions for regional focus option
  const { regions, isLoading: regionsLoading } = useRegionsByNation(party?.nation_id)

  // Fetch active scandals for damage control
  const { scandals, isLoading: scandalsLoading, hasScandals } = useActiveScandals(
    partyId,
    currentTick
  )

  // Fetch recent successes for highlight success
  const { successes, isLoading: successesLoading, hasSuccesses } = useRecentSuccesses(
    partyId,
    currentTick
  )

  // Create action mutation
  const { createAction, isCreating, error } = useCreateAction({
    onSuccess: () => {
      onSuccess?.()
      onOpenChange(false)
    },
  })

  // Get selected scandal info
  const selectedScandal = useMemo(() => {
    if (!scandalId || !scandals) return null
    return scandals.find((s) => s.id === scandalId)
  }, [scandalId, scandals])

  // Get selected success info
  const selectedSuccess = useMemo(() => {
    if (!successEventId || !successes) return null
    return successes.find((s) => s.id === successEventId)
  }, [successEventId, successes])

  // Get selected region info
  const selectedRegion = useMemo(() => {
    if (focus !== 'regional' || !regionId || !regions) return null
    return regions.find((r) => r.id === regionId)
  }, [focus, regionId, regions])

  // Calculate effect preview based on selections
  const effectPreview = useMemo(() => {
    switch (prType) {
      case 'general_messaging': {
        const effects = calculateGeneralMessagingEffects(intensity, focus === 'regional')
        const scopeText = focus === 'regional' && selectedRegion
          ? selectedRegion.name
          : 'nationally'
        return {
          description: `+${effects.recognition} recognition ${scopeText}, +${effects.momentum} momentum`,
          recognition: effects.recognition,
          momentum: effects.momentum,
        }
      }
      case 'damage_control': {
        const effects = calculateDamageControlEffects(intensity, strategy)
        return {
          description: `Reduce scandal impact by ~${effects.impactReduction}%, accelerate decay by ${effects.decayAcceleration} tick(s)`,
          impactReduction: effects.impactReduction,
          decayAcceleration: effects.decayAcceleration,
          riskNote: effects.riskNote,
        }
      }
      case 'highlight_success': {
        const effects = calculateHighlightSuccessEffects(intensity, amplificationScope === 'national')
        const scopeText = amplificationScope === 'national' ? 'nationally' : 'in targeted regions'
        return {
          description: `+${effects.recognition} recognition ${scopeText}, +${effects.momentum} momentum`,
          recognition: effects.recognition,
          momentum: effects.momentum,
        }
      }
    }
  }, [prType, intensity, focus, selectedRegion, strategy, amplificationScope])

  // Validation
  const canSubmit = useMemo(() => {
    if (isCreating || intensity > availableAP) return false

    switch (prType) {
      case 'general_messaging':
        // Regional focus requires region selection
        if (focus === 'regional' && !regionId) return false
        return true
      case 'damage_control':
        // Must have a scandal selected and a strategy
        if (!scandalId || !strategy) return false
        return true
      case 'highlight_success':
        // Must have a success event selected
        if (!successEventId) return false
        return true
    }
  }, [isCreating, intensity, availableAP, prType, focus, regionId, scandalId, strategy, successEventId])

  const apAfterAction = availableAP - intensity
  const minAP = PR_EFFECTS.MIN_AP
  const maxAP = Math.min(PR_EFFECTS.MAX_AP, availableAP)

  const handleSubmit = () => {
    if (!canSubmit) return

    const parameters: Record<string, unknown> = {}

    switch (prType) {
      case 'general_messaging':
        parameters.focus = focus
        if (focus === 'regional' && regionId) {
          parameters.region_id = regionId
          parameters.region_name = selectedRegion?.name
        }
        if (theme) {
          parameters.theme = theme
        }
        break
      case 'damage_control':
        parameters.event_id = scandalId
        parameters.strategy = strategy
        if (selectedScandal) {
          parameters.scandal_type = selectedScandal.scandal_type
          parameters.scandal_severity = selectedScandal.severity
        }
        break
      case 'highlight_success':
        parameters.success_event_id = successEventId
        parameters.amplification_scope = amplificationScope
        if (selectedSuccess) {
          parameters.success_type = selectedSuccess.event_type
          parameters.success_name = selectedSuccess.name
        }
        break
    }

    createAction({
      party_id: partyId,
      tick: currentTick,
      action_type: 'pr',
      action_subtype: prType,
      target_type: 'party',
      target_id: partyId, // PR targets own party
      action_points_spent: intensity,
      parameters,
    })
  }

  const handleBack = () => {
    onBack?.()
    onOpenChange(false)
  }

  // Reset type-specific state when PR type changes
  const handlePrTypeChange = (newType: PRSubtype) => {
    setPrType(newType)
    // Reset fields when switching types
    if (newType === 'general_messaging') {
      setFocus('balanced')
      setRegionId('')
      setTheme('')
    } else if (newType === 'damage_control') {
      setScandalId('')
      setStrategy('apologize')
    } else if (newType === 'highlight_success') {
      setSuccessEventId('')
      setAmplificationScope('national')
    }
  }

  // Combined loading state (used for potential future loading indicators)
  const _isLoading = partyLoading || regionsLoading || scandalsLoading || successesLoading
  void _isLoading // Acknowledge unused but keep for potential future use

  // Helper to format scandal severity
  const getSeverityColor = (severity: ScandalSeverity) => {
    switch (severity) {
      case 'minor': return 'text-yellow-600 bg-yellow-100 dark:bg-yellow-900/30'
      case 'moderate': return 'text-orange-600 bg-orange-100 dark:bg-orange-900/30'
      case 'major': return 'text-red-600 bg-red-100 dark:bg-red-900/30'
    }
  }

  // Helper to get ticks since scandal
  const getTicksSince = (discoveredTick: number) => {
    return currentTick - discoveredTick
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span>📢</span> Configure PR Action
          </DialogTitle>
          <DialogDescription>
            Shape public perception through strategic communications. Build recognition,
            manage scandals, or amplify successes.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* PR Type Selection */}
          <div className="space-y-3">
            <Label>PR Type</Label>
            <div className="grid grid-cols-1 gap-2">
              {(Object.entries(PR_SUBTYPES) as [PRSubtype, typeof PR_SUBTYPES[PRSubtype]][]).map(
                ([type, config]) => {
                  const isSelected = prType === type
                  const isDisabled =
                    (type === 'damage_control' && !hasScandals) ||
                    (type === 'highlight_success' && !hasSuccesses)

                  return (
                    <button
                      key={type}
                      type="button"
                      onClick={() => !isDisabled && handlePrTypeChange(type)}
                      disabled={isDisabled}
                      className={cn(
                        'rounded-lg border-2 p-3 text-left transition-all',
                        isDisabled
                          ? 'cursor-not-allowed border-muted bg-muted/30 opacity-60'
                          : 'hover:border-primary/50 hover:bg-accent/50',
                        isSelected && !isDisabled
                          ? 'border-primary bg-accent'
                          : 'border-border bg-background'
                      )}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-2">
                          <span className="text-xl">{config.icon}</span>
                          <div>
                            <span className="font-medium">{config.label}</span>
                            {isDisabled && (
                              <span className="ml-2 text-xs text-muted-foreground">
                                (unavailable)
                              </span>
                            )}
                          </div>
                        </div>
                        {isSelected && !isDisabled && (
                          <div className="bg-primary text-primary-foreground rounded-full p-0.5">
                            <CheckCircle2 className="h-3 w-3" />
                          </div>
                        )}
                      </div>
                      <p className="text-muted-foreground mt-1 text-xs">
                        {config.description}
                      </p>
                      {isDisabled && type === 'damage_control' && (
                        <p className="text-muted-foreground mt-1 text-xs italic">
                          No active scandals to address
                        </p>
                      )}
                      {isDisabled && type === 'highlight_success' && (
                        <p className="text-muted-foreground mt-1 text-xs italic">
                          No recent successes to highlight
                        </p>
                      )}
                    </button>
                  )
                }
              )}
            </div>
          </div>

          {/* General Messaging Options */}
          {prType === 'general_messaging' && (
            <>
              {/* Focus Selection */}
              <div className="space-y-2">
                <Label>Focus</Label>
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    type="button"
                    variant={focus === 'balanced' ? 'default' : 'outline'}
                    className="w-full"
                    onClick={() => {
                      setFocus('balanced')
                      setRegionId('')
                    }}
                  >
                    Balanced
                  </Button>
                  <Button
                    type="button"
                    variant={focus === 'regional' ? 'default' : 'outline'}
                    className="w-full"
                    onClick={() => setFocus('regional')}
                  >
                    Regional Focus
                  </Button>
                </div>
                <p className="text-muted-foreground text-xs">
                  {focus === 'balanced'
                    ? 'Equal recognition boost across all districts'
                    : 'Concentrated effect in selected region (+50% effectiveness)'}
                </p>
              </div>

              {/* Region Selector (for regional focus) */}
              {focus === 'regional' && (
                <div className="space-y-2">
                  <Label htmlFor="region-select">Select Region</Label>
                  {regionsLoading ? (
                    <div className="flex h-9 items-center justify-center">
                      <Loader2 className="text-muted-foreground h-4 w-4 animate-spin" />
                    </div>
                  ) : (
                    <Select value={regionId} onValueChange={setRegionId}>
                      <SelectTrigger id="region-select">
                        <SelectValue placeholder="Choose a region..." />
                      </SelectTrigger>
                      <SelectContent>
                        {regions?.map((region) => (
                          <SelectItem key={region.id} value={region.id}>
                            {region.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              )}

              {/* Message Theme (optional flavor) */}
              <div className="space-y-2">
                <Label htmlFor="theme-select">
                  Message Theme <span className="text-muted-foreground">(optional)</span>
                </Label>
                <Select value={theme} onValueChange={setTheme}>
                  <SelectTrigger id="theme-select">
                    <SelectValue placeholder="Select a theme..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">No specific theme</SelectItem>
                    {MESSAGE_THEMES.map((t) => (
                      <SelectItem key={t.key} value={t.key}>
                        {t.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {theme && (
                  <p className="text-muted-foreground text-xs">
                    {MESSAGE_THEMES.find((t) => t.key === theme)?.description}
                  </p>
                )}
              </div>
            </>
          )}

          {/* Damage Control Options */}
          {prType === 'damage_control' && (
            <>
              {/* Scandal Selector */}
              <div className="space-y-2">
                <Label htmlFor="scandal-select">Select Scandal to Address</Label>
                {scandalsLoading ? (
                  <div className="flex h-9 items-center justify-center">
                    <Loader2 className="text-muted-foreground h-4 w-4 animate-spin" />
                  </div>
                ) : scandals.length === 0 ? (
                  <div className="flex items-center gap-2 rounded-lg border border-muted bg-muted/30 p-3">
                    <Info className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">
                      No active scandals to address
                    </span>
                  </div>
                ) : (
                  <Select value={scandalId} onValueChange={setScandalId}>
                    <SelectTrigger id="scandal-select">
                      <SelectValue placeholder="Choose a scandal..." />
                    </SelectTrigger>
                    <SelectContent>
                      {scandals.map((scandal) => (
                        <SelectItem key={scandal.id} value={scandal.id}>
                          <div className="flex items-center gap-2">
                            <span
                              className={cn(
                                'rounded px-1.5 py-0.5 text-xs font-medium',
                                getSeverityColor(scandal.severity)
                              )}
                            >
                              {scandal.severity}
                            </span>
                            <span>{SCANDAL_TYPES[scandal.scandal_type].label}</span>
                            <span className="text-muted-foreground text-xs">
                              ({getTicksSince(scandal.discovered_tick)} tick(s) ago)
                            </span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                {selectedScandal && (
                  <p className="text-muted-foreground text-xs">
                    {SCANDAL_TYPES[selectedScandal.scandal_type].description}
                  </p>
                )}
              </div>

              {/* Strategy Selection */}
              <div className="space-y-3">
                <Label>Response Strategy</Label>
                <div className="space-y-2">
                  {DAMAGE_CONTROL_STRATEGY_ORDER.map((strategyKey) => {
                    const config = DAMAGE_CONTROL_STRATEGIES[strategyKey]
                    const isSelected = strategy === strategyKey
                    return (
                      <button
                        key={strategyKey}
                        type="button"
                        onClick={() => setStrategy(strategyKey)}
                        className={cn(
                          'w-full rounded-lg border-2 p-3 text-left transition-all',
                          'hover:border-primary/50 hover:bg-accent/50',
                          isSelected
                            ? 'border-primary bg-accent'
                            : 'border-border bg-background'
                        )}
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{config.label}</span>
                              <span
                                className={cn(
                                  'rounded px-1.5 py-0.5 text-xs font-medium',
                                  config.riskLevel === 'low'
                                    ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                    : config.riskLevel === 'medium'
                                      ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                                      : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                                )}
                              >
                                {config.riskLevel} risk
                              </span>
                            </div>
                            <p className="text-muted-foreground mt-1 text-xs">
                              {config.description}
                            </p>
                          </div>
                          {isSelected && (
                            <div className="bg-primary text-primary-foreground rounded-full p-0.5">
                              <CheckCircle2 className="h-3 w-3" />
                            </div>
                          )}
                        </div>
                      </button>
                    )
                  })}
                </div>
                {strategy && (
                  <p className="text-muted-foreground text-xs italic">
                    {DAMAGE_CONTROL_STRATEGIES[strategy].riskDescription}
                  </p>
                )}
              </div>
            </>
          )}

          {/* Highlight Success Options */}
          {prType === 'highlight_success' && (
            <>
              {/* Success Selector */}
              <div className="space-y-2">
                <Label htmlFor="success-select">Select Success to Highlight</Label>
                {successesLoading ? (
                  <div className="flex h-9 items-center justify-center">
                    <Loader2 className="text-muted-foreground h-4 w-4 animate-spin" />
                  </div>
                ) : successes.length === 0 ? (
                  <div className="flex items-center gap-2 rounded-lg border border-muted bg-muted/30 p-3">
                    <Info className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">
                      No recent successes to highlight
                    </span>
                  </div>
                ) : (
                  <Select value={successEventId} onValueChange={setSuccessEventId}>
                    <SelectTrigger id="success-select">
                      <SelectValue placeholder="Choose a success..." />
                    </SelectTrigger>
                    <SelectContent>
                      {successes.map((success) => (
                        <SelectItem key={success.id} value={success.id}>
                          <div className="flex items-center gap-2">
                            <span className="rounded bg-green-100 px-1.5 py-0.5 text-xs font-medium text-green-700 dark:bg-green-900/30 dark:text-green-400">
                              {success.event_type.replace('_', ' ')}
                            </span>
                            <span>{success.name}</span>
                            <span className="text-muted-foreground text-xs">
                              (+{success.potential_momentum} momentum)
                            </span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                {selectedSuccess && (
                  <p className="text-muted-foreground text-xs">
                    {selectedSuccess.description}
                  </p>
                )}
              </div>

              {/* Amplification Scope */}
              <div className="space-y-2">
                <Label>Amplification Scope</Label>
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    type="button"
                    variant={amplificationScope === 'national' ? 'default' : 'outline'}
                    className="w-full"
                    onClick={() => setAmplificationScope('national')}
                  >
                    National
                  </Button>
                  <Button
                    type="button"
                    variant={amplificationScope === 'targeted' ? 'default' : 'outline'}
                    className="w-full"
                    onClick={() => setAmplificationScope('targeted')}
                  >
                    Targeted
                  </Button>
                </div>
                <p className="text-muted-foreground text-xs">
                  {amplificationScope === 'national'
                    ? 'Spread recognition boost nationally (wider but thinner effect)'
                    : 'Concentrate effect where success occurred (stronger local impact)'}
                </p>
              </div>
            </>
          )}

          {/* Intensity Slider */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label>Intensity</Label>
              <span className="text-sm font-medium">{intensity} AP</span>
            </div>
            <Slider
              value={[intensity]}
              onValueChange={([value]) => setIntensity(value)}
              min={minAP}
              max={maxAP}
              step={1}
              disabled={availableAP < minAP}
            />
            <div className="text-muted-foreground flex justify-between text-xs">
              <span>1 - Light</span>
              <span>2 - Standard</span>
              <span>3 - Intensive</span>
            </div>
          </div>

          {/* Effect Preview */}
          <div className="bg-muted/50 space-y-2 rounded-lg border p-4">
            <h4 className="text-sm font-medium">Expected Effect</h4>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Primary effect:</span>
                <span className="font-medium text-green-600 dark:text-green-400">
                  {effectPreview.description}
                </span>
              </div>
              {prType === 'damage_control' && effectPreview.riskNote && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Note:</span>
                  <span className="text-muted-foreground text-xs">
                    {effectPreview.riskNote}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* AP Summary */}
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">AP after action:</span>
            <span className={cn('font-medium', apAfterAction < 0 && 'text-destructive')}>
              {availableAP} → {Math.max(0, apAfterAction)}
            </span>
          </div>

          {/* Insufficient AP warning */}
          {availableAP < minAP && (
            <div className="flex items-center gap-2 text-destructive text-sm">
              <AlertCircle className="h-4 w-4" />
              <span>
                PR actions require at least {minAP} AP. You have {availableAP} AP available.
              </span>
            </div>
          )}

          {/* Error display */}
          {error && (
            <div className="flex items-center gap-2 text-destructive text-sm">
              <AlertCircle className="h-4 w-4" />
              <span>{error.message || 'Failed to queue action'}</span>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={handleBack} disabled={isCreating}>
            Back
          </Button>
          <Button type="button" onClick={handleSubmit} disabled={!canSubmit}>
            {isCreating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isCreating ? 'Queuing...' : 'Queue PR Action'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
